import { useEffect, useState, useRef } from "react";
import {
  AppBar, Box, Toolbar, Typography, Container, Button,
  useTheme, styled, Badge, useMediaQuery, IconButton,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import bfLogo from "../../assets/images/utsav-pack-logo.png";
import { Link, NavLink, useNavigate } from "react-router";
import { MagnifyingGlass, ShoppingCartSimple, UserCircle } from "@phosphor-icons/react";
import GeneralLoader from "./GeneralLoader";
import { useLoading } from "../../hooks/LoadingContext";
import SearchBar from "./SearchBar";
import { useAuth } from "../../hooks/AuthContext";
import RegionDialog from "./RegionDialog";
import {
  getAllActiveRegionList,
  getPaymentDetails,
  // ✅ Removed getProductsForUniversalSearch
} from "../../utilities/service";
import useApiQuery from "../../hooks/useApiQuery";
import useAutoDetectCity from "./AutoDetectRegion";
import BouncyMapPin from "../../animations/BouncyMapPin";
import LoginDialog from "../home/LoginDialog";
import RegionBar from "./RegionBar";
import LogoLoader from "../../animations/LogoLoader";
import InventoryIcon from "@mui/icons-material/Inventory";

const cartAnimationStyles = `
  @keyframes cartShake {
    0%   { transform: rotate(0deg) scale(1); }
    15%  { transform: rotate(-18deg) scale(1.15); }
    30%  { transform: rotate(18deg) scale(1.15); }
    45%  { transform: rotate(-12deg) scale(1.1); }
    60%  { transform: rotate(12deg) scale(1.1); }
    75%  { transform: rotate(-6deg) scale(1.05); }
    90%  { transform: rotate(6deg) scale(1.05); }
    100% { transform: rotate(0deg) scale(1); }
  }
  @keyframes itemDrop {
    0%   { transform: translateX(-50%) translate(-6px, -24px) rotate(-15deg); opacity: 1; }
    60%  { transform: translateX(-50%) translate(3px, 8px) rotate(8deg); opacity: 1; }
    80%  { transform: translateX(-50%) translate(1px, 2px) rotate(4deg); opacity: 0.8; }
    100% { transform: translateX(-50%) translate(0px, 4px) rotate(0deg); opacity: 0; }
  }
  @keyframes ripplePulse {
    0%   { transform: scale(1); opacity: 0.6; }
    100% { transform: scale(2.2); opacity: 0; }
  }
  .cart-shake { animation: cartShake 0.55s cubic-bezier(0.36, 0.07, 0.19, 0.97) both; }
  .cart-drop-dot {
    position: absolute; top: -14px; left: 50%;
    transform: translateX(-50%); width: auto; height: auto;
    border-radius: 0; background: transparent;
    animation: itemDrop 1.2s ease forwards;
    pointer-events: none; z-index: 999;
  }
  .cart-ripple {
    position: absolute; inset: -4px; border-radius: 50%;
    border: 2px solid var(--primary-red-base, #C49A27);
    animation: ripplePulse 0.55s ease forwards; pointer-events: none;
  }
`;

if (typeof document !== "undefined" && !document.getElementById("cart-anim-styles-v3")) {
  const style = document.createElement("style");
  style.id = "cart-anim-styles-v3";
  style.innerHTML = cartAnimationStyles;
  document.head.appendChild(style);
}

function AnimatedCartIcon({ cartCount, isActive, theme }) {
  const [animating, setAnimating] = useState(false);
  const [showDrop, setShowDrop] = useState(false);
  const prevCountRef = useRef(cartCount);

  useEffect(() => {
    const prev = prevCountRef.current;
    const curr = cartCount;
    if (curr !== prev) {
      if (curr > prev) {
        setShowDrop(true);
        setTimeout(() => setShowDrop(false), 1200);
      }
      setAnimating(true);
      setTimeout(() => setAnimating(false), 560);
      prevCountRef.current = curr;
    }
  }, [cartCount]);

  const iconColor = isActive ? theme.palette.primary.main : theme.palette.text.neural_600;

  return (
    <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
      {animating && <span className="cart-ripple" />}
      {showDrop && (
        <InventoryIcon
          className="cart-drop-dot"
          style={{ width: "14px", height: "14px", fontSize: "14px", color: "var(--primary-red-base, #C49A27)", padding: 0, margin: 0 }}
        />
      )}
      <Box className={animating ? "cart-shake" : ""} sx={{ display: "flex", alignItems: "center" }}>
        {cartCount > 0 ? (
          <Badge badgeContent={cartCount} color="primary">
            <ShoppingCartSimple size={24} color={iconColor} />
          </Badge>
        ) : (
          <ShoppingCartSimple size={24} color={iconColor} />
        )}
      </Box>
    </Box>
  );
}

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  position: "sticky",
  top: 0,
  transition: "all 0.25s ease",
  border: "none",
  borderBottom: "1px solid rgba(185, 146, 72, 0.18)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  background: "rgba(255,255,255,0.96)",
  backdropFilter: "blur(8px)",
  boxShadow: "0 10px 24px rgba(35, 32, 28, 0.04)",
  zIndex: 1000,
  [theme.breakpoints.down("md")]: { height: `${65}px` },
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  minHeight: "96px",
  padding: "0 24px",
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "16px",
  "@media (min-width: 900px) and (max-width: 1100px)": {
    padding: "0 12px",
    gap: "8px",
  },
  [theme.breakpoints.down("md")]: {
    padding: "0",
    minHeight: "auto",
    display: "flex",
    justifyContent: "space-between",
  },
}));

const LocationButton = styled(Button)(({ theme }) => ({
  fontWeight: 600,
  textTransform: "none",
  fontSize: "15px",
  padding: "8px 14px",
  borderRadius: "999px",
  border: "1px solid rgba(183,123,26,0.18)",
  background: "rgba(244,239,230,0.56)",
  color: "var(--primary-red-600)",
  display: "flex",
  alignItems: "center",
  gap: 8,
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "rgba(244,239,230,0.9)",
    boxShadow: "none",
  },
  "& .MuiSvgIcon-root": {
    color: "var(--primary-red-base)",
    fontSize: "22px !important",
    [theme.breakpoints.down("sm")]: { fontSize: "20px !important" },
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
    padding: "6px 10px",
    gap: 4,
  },
}));

const NavText = styled(Typography)(({ theme }) => ({
  color: "var(--neutral-700)",
  fontWeight: 600,
  fontSize: "15px",
  lineHeight: "24px",
  letterSpacing: "0.01em",
  [theme.breakpoints.down("md")]: { display: "none" },
}));

const MobileSearchIconNavLink = styled(NavLink)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  [theme.breakpoints.up("sm")]: { display: "none" },
}));

function Navbar() {
  const { user, setUser, setLoginDialogOpen, setHelperFunction, loginDialogOpen } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { isLoading } = useLoading();

  // ✅ Removed getProductsForUniversalSearch query — SearchBar handles it internally now

  const { data: paymentResponseData } = useApiQuery({
    queryKey: ["getPaymentDetails", user?.region_id, user?.PAYMENT_KEY],
    queryFn: () => getPaymentDetails(user?.region_id, setUser),
    enabled: user?.region_id != null,
  });
  const handleClickOpen = () => setLoginDialogOpen(true);

  const routeHandler = (path) => {
    if (!user?.customer_id) {
      setHelperFunction({ to: path, exeFunction: () => navigate(path) });
      setLoginDialogOpen(true);
    } else {
      navigate(path);
    }
  };

  return (
    <>
      <StyledAppBar position="static">
        <Container maxWidth="false" sx={{ py: 0.2 }}>
          <StyledToolbar disableGutters>
            {/* Left Section */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0, minWidth: 0 }}>
              <Link to="/" style={{ textDecoration: "none" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, maxWidth: 300 }}>
                  <Box
                    component="img"
                    src={bfLogo}
                    alt="Utsav Pack"
                    sx={{
                      height: { xs: "40px", md: "48px", lg: "54px" },
                      width: "auto",
                      flexShrink: 0,
                      transition: "0.25s ease",
                      filter: "drop-shadow(0px 0px 0px rgba(183,123,26,0.15))",
                      "&:hover": {
                        filter: "drop-shadow(0px 0px 6px rgba(183,123,26,0.18))",
                      },
                    }}
                  />

                </Box>
              </Link>
            </Box>

            {/* ✅ SearchBar now handles its own data fetching */}
            <Box sx={{ flex: "1 1 auto", minWidth: 0, maxWidth: 720, width: "100%" }}>
              <SearchBar />
            </Box>

            {/* Right Section */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, justifyContent: "flex-end", flexShrink: 0 }}>
              <MobileSearchIconNavLink to="/search" aria-label="Open search">
                {({ isActive }) => (
                  <>
                    <MagnifyingGlass size={24} color={isActive ? theme.palette.primary.main : theme.palette.text.neural_600} />
                    <NavText style={{ color: isActive ? theme.palette.primary.main : theme.palette.text.neural_black }}>Search</NavText>
                  </>
                )}
              </MobileSearchIconNavLink>

              <NavLink
                to="/cart" aria-label="View cart"
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
                onClick={(e) => {
                  if (!user?.region_id || !user?.customer_id) {
                    e.preventDefault();
                    routeHandler("/cart");
                  }
                }}
              >
                {({ isActive }) => (
                  <>
                    <AnimatedCartIcon cartCount={user?.cartCount ?? 0} isActive={isActive} theme={theme} />
                    <NavText style={{ color: isActive ? theme.palette.primary.main : theme.palette.text.neural_black }}>Cart</NavText>
                  </>
                )}
              </NavLink>

              {user?.customer_id ? (
                <NavLink to="account/profile" aria-label="View profile" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  {({ isActive }) => (
                    <>
                      <UserCircle size={24} color={isActive ? theme.palette.primary.main : theme.palette.text.neural_600} />
                      <NavText style={{ color: isActive ? theme.palette.primary.main : theme.palette.text.neural_black }}>Profile</NavText>
                    </>
                  )}
                </NavLink>
              ) : (
                <Button
                  data-testid="login-btn"
                  onClick={handleClickOpen}
                  variant="outlined"
                  sx={{
                    bgcolor: "var(--neutral-white)",
                    borderRadius: "999px",
                    padding: "9px 20px",
                    minWidth: "92px",
                    height: "40px",
                    fontWeight: 700,
                    fontSize: "14px",
                    color: "var(--primary-red-600)",
                    border: "1px solid rgba(183,123,26,0.28)",
                    boxShadow: "0 6px 14px rgba(183,123,26,0.08)",
                    "&:hover": {
                      bgcolor: "rgba(183,123,26,0.04)",
                      borderColor: "rgba(183,123,26,0.4)",
                    },
                  }}
                >
                  Login
                </Button>
              )}
            </Box>
          </StyledToolbar>
        </Container>
        {isLoading && !isMobile && <GeneralLoader />}
      </StyledAppBar>

      {loginDialogOpen && <LoginDialog />}
    </>
  );
}

export default Navbar;
